import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { getExamSessionDetailsById } from "@services/adminDashboard";
import StudentListUpload from "@modals/studentlistupload/StudentListUpload";
import UploadAnswerSheets from "@modals/uploadanswersheet/UploadAnswerSheets";
import { subjectIDs } from "@constants/subject";
import backArrow from "@assets/icons/arrow-left.svg";
import arrowDown from "@assets/icons/arrow-down.svg";
import fileUploadIcon from "@assets/icons/upload-icon.svg";
import fileIcon from "@assets/icons/file-upload.svg";
import topperIcon from "@assets/icons/topper-icon.svg";
import clockIcon from "@assets/icons/clock-icon.svg";
import chevronLeft from "@assets/icons/chevron-left.svg";
import chevronRight from "@assets/icons/chevron-right.svg";
import pdfIcon from "@assets/icons/pdf-icon.svg";
import downloadIcon from "@assets/icons/download-icon.svg";
import personIcon from "@assets/icons/person-icon.svg";
import pendingIcon from "@assets/icons/pending-icon.svg";
import uploadSuccessIcon from "@assets/icons/upload-success-icon.svg";
import uploadFailIcon from "@assets/icons/upload-fail-icon.svg";
import styles from "./ExamSession.module.css";
import { setComponentToShow } from "@store/ModalToShow";
import AssignEvaluators from "@modals/assignevaluators/AssignEvaluators";
import UploadEvaluatorDetails from "@modals/uploadevaluatordetails/UploadEvaluatorDetails";
import { getExamSubmissionIdByExamId } from "@services/answersheetUpload";
import { setSessionStudentUploadData } from "@store/admin/AdminDashboardStore";
import { environment } from "@environments/environment";
import Loader from "@components/loader/Loader";
import ScanToUploadFile from "@modals/scantouploadfile/ScanToUploadFile";
import { EXAM_SUBMISSIONS_LIMIT } from "@constants/appConstants";

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function ExamSession() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pageLocation = useLocation();
  const dispatch = useDispatch();

  const modalName = useSelector(
    (state: any) => state.modalToShow.componentName
  );
  const [examSessionId, setExamSessionId] = useState<string>("");
  const [questionPaperId, setQuestionPaperId] = useState<string>("");
  const [examWorkflowStateId, setExamWorkflowStateId] = useState<string>(
    environment.evaluatorWorkflowStateId
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [examSessionData, setExamSessionData] = useState<any>(null);
  const [evaluatorData, setEvaluatorData] = useState<any[]>([]);
  const [examSessionSubmissions, setExamSessionSubmissions] = useState<any[]>(
    []
  );
  const [studentFilePendingUploads, setStudentFilePendingUploads] =
    useState<number>(0);
  const [studentFileSuccessUploads, setStudentFileSuccessUploads] =
    useState<number>(0);
  const [studentFileFailedUploads, setStudentFileFailedUploads] =
    useState<number>(0);
  const [failedUploadsReport, setFailedUploadsReport] = useState<any[]>([]);
  const isFromElectron = window.electronAPI || false;
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);

  const goBackToDashboard = () => {
    dispatch(setComponentToShow(""));
    dispatch(setSessionStudentUploadData([]));
    navigate("/admin-dashboard");
  };

  const fetchExamDetails = async (examId: string) => {
    setIsLoading(true);
    try {
      const res = await getExamSessionDetailsById(examId);
      if (res) {
        setExamSessionData(res);
      }
      if (
        res.evaluation_workflow.phases.length > 1 &&
        res?.evaluation_workflow?.phases?.[1].evaluator_pools?.length > 0
      ) {
        setEvaluatorData(res.evaluation_workflow?.phases?.[1].evaluator_pools);
      } else {
        dispatch(setComponentToShow("AssignEvaluators"));
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const assignEvaluators = (examSessionData: any) => {
    setExamSessionData(examSessionData);
    setEvaluatorData(
      examSessionData?.evaluation_workflow?.phases?.[1].evaluator_pools
    );
    dispatch(setComponentToShow(""));
  };

  const fetchStudentSubmissionDetails = async (
    examSessionId: string,
    cursor: string | null = null
  ) => {
    if (cursor) {
      setIsFetchingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const res = await getExamSubmissionIdByExamId(examSessionId, cursor, EXAM_SUBMISSIONS_LIMIT);
      if (res && res.items) {
        if (cursor) {
          setExamSessionSubmissions((prev) => [...prev, ...res.items]);
          setStudentFileSuccessUploads((prev) => prev + res.items.length);
        } else {
          setExamSessionSubmissions(res.items);
          setStudentFileSuccessUploads(res.items.length);
        }
        setNextCursor(res.next_cursor || null);
        setHasMore(res.has_more || false);
      }
    } catch (error) {
      console.error(error);
    }

    if (cursor) {
      setIsFetchingMore(false);
    } else {
      setIsLoading(false);
    }
  };

  const handleScroll = (e: any) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50;
    if (bottom && hasMore && !isFetchingMore && !isLoading && nextCursor) {
      fetchStudentSubmissionDetails(examSessionId, nextCursor);
    }
  };

  const downloadFailedReport = () => {
    if (failedUploadsReport.length === 0) return;
    const csvContent =
      "data:text/csv;charset=utf-8," +
      ["student_identifier,original_filename,reason"]
        .concat(
          failedUploadsReport.map(
            (item) => `${item.student_id},${item.filename},${item.reason}`
          )
        )
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "failed_uploads_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderModals = () => {
    switch (modalName) {
      case "AssignEvaluators":
        return (
          <AssignEvaluators
            isOpen={true}
            onAssignEvaluators={() =>
              dispatch(setComponentToShow("UploadEvaluatorDetails"))
            }
            onCancel={() => goBackToDashboard()}
            onClose={() => goBackToDashboard()}
          />
        );
      case "UploadEvaluatorDetails":
        return (
          <UploadEvaluatorDetails
            isOpen={true}
            examSessionId={examSessionId}
            questionPaperId={questionPaperId}
            examWorkflowStateId={
              examSessionData.evaluation_workflow.phases[1].workflow_state_id ||
              examWorkflowStateId
            }
            handleSave={assignEvaluators}
            onClose={() => goBackToDashboard()}
          />
        );
      case "UploadStudentDetails":
        return (
          <StudentListUpload
            isOpen={true}
            examSessionId={examSessionId}
            questionPaperId={questionPaperId}
            handleSave={() =>
              dispatch(setComponentToShow("UploadAnswerSheets"))
            }
            onClose={() => dispatch(setComponentToShow(""))}
          />
        );
      case "UploadAnswerSheets":
        return (
          <UploadAnswerSheets
            isOpen={true}
            examSessionId={examSessionId}
            questionPaperId={questionPaperId}
            onCompleteAnswerSheetUpload={async (failedUploads: any[] = []) => {
              setFailedUploadsReport(failedUploads);
              await fetchStudentSubmissionDetails(examSessionId);
              dispatch(setComponentToShow(""));
            }}
            onClose={() => dispatch(setComponentToShow(""))}
          />
        );
      case "ScanToUploadFile":
        return(
          <ScanToUploadFile examSessionId={examSessionId} questionPaperId={questionPaperId} isOpen={true} onClose={() => dispatch(setComponentToShow(""))} />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      const exam_id = searchParams.get("exam_id");
      const question_paper_id = searchParams.get("question_paper_id");
      dispatch(setSessionStudentUploadData([]));
      if (exam_id) {
        setExamSessionId(exam_id);
        await fetchExamDetails(exam_id);
        await fetchStudentSubmissionDetails(exam_id);
      }
      if (question_paper_id) {
        setQuestionPaperId(question_paper_id);
      }
    };

    initializeData();
  }, [searchParams]);

  useEffect(() => {
    const exam_id = searchParams.get("exam_id");
    if (exam_id && modalName === "") {
      fetchStudentSubmissionDetails(exam_id);
    }
  }, [modalName]);

  if (isLoading) {
    return <Loader />;
  }
  return (
    <div className={styles.examSessionContainer}>
      <div className={styles.backButtonContainer}>
        <button className={styles.backButton} onClick={goBackToDashboard}>
          <img src={backArrow} alt="Back" className={styles.arrowDown} /> Back
        </button>
      </div>
      <div className={styles.testDetails}>
        <h1 className={styles.testTitle}>{examSessionData?.name}</h1>
        {examSessionData?.exam_date && (
          <div className={styles.testDate}>
            {formatDate(examSessionData?.exam_date)}
          </div>
        )}
        <div className={styles.testInfo}>
          {examSessionData?.grade_ids?.length > 0 && (
            <>
              <div className={styles.testInfoItem}>
                <img src={topperIcon} alt="Topper" />
                {examSessionData.grade_ids.map((gradeId: number) => (
                  <span>{`Grade ${gradeId}`}</span>
                ))}
              </div>
              <div className={styles.testInfoItem}>
                <span>|</span>
              </div>
            </>
          )}
          {examSessionData?.duration && (
            <>
              <div className={styles.testInfoItem}>
                <img src={clockIcon} alt="Clock" />
                <span>{`${examSessionData?.duration} mins`}</span>
              </div>
              <div className={styles.testInfoItem}>
                <span>|</span>
              </div>
            </>
          )}
          {examSessionData?.subject_ids?.length > 0 && (
            <>
              <div className={styles.testInfoItem}>
                {examSessionData.subject_ids.map((subjectId: number) => (
                  <span>{subjectIDs[subjectId]}</span>
                ))}
              </div>
              <div className={styles.testInfoItem}>
                <span>|</span>
              </div>
            </>
          )}
          {examSessionData?.totalMarks && (
            <>
              <div className={styles.testInfoItem}>
                <span>{`${examSessionData?.totalMarks} Marks`}</span>
              </div>
              <div className={styles.testInfoItem}>
                <span>|</span>
              </div>
            </>
          )}
        </div>
        <div className={styles.studentsInfo}>
          <div>
            {examSessionSubmissions?.length > 0 && (
              <h2>{`Students (${examSessionSubmissions?.length})`}</h2>
            )}
          </div>
          <div className={styles.uploadButtons}>
            {failedUploadsReport.length > 0 && (
            <button
              className={styles.uploadAnswerButton}
              onClick={downloadFailedReport}
            >
              DOWNLOAD FAILED REPORT
              <img src={downloadIcon} alt="download" />
            </button>
          )}
          <button
            className={styles.uploadAnswerButton}
            onClick={() => dispatch(setComponentToShow("UploadStudentDetails"))}
          >
            UPLOAD STUDENTS DETAILS
          </button>
          {isFromElectron && <button
            className={styles.uploadAnswerButton}
            onClick={() => dispatch(setComponentToShow("ScanToUploadFile"))}
          >
            SCAN & UPLOAD STUDENT ANSWERSHEET
          </button>}
          </div>
        </div>
      </div>

      {examSessionSubmissions.length > 0 ? (
        <div className={styles.uploadedList}>          
          <div className={styles.tableContainer} onScroll={handleScroll}>
            <table className={styles.testListTable}>
                <thead>
                    <tr>
                        <th>Student ID</th>
                        <th>Submission Document</th>
                        <th>Submission Date</th>
                    </tr>
                </thead>
                <tbody>
                    {examSessionSubmissions.map((submission: any) => (
                        <tr key={submission.id}>
                            <td>{submission.submitted_by}</td>
                            <td>
                                {submission.question_paper_submissions_documents?.map((doc: any) => (
                                    <a key={doc.document.id} href={doc.document.signed_url} target="_blank" rel="noopener noreferrer" className={styles.docLink}>
                                        <img src={pdfIcon} alt="PDF" className={styles.pdfIcon} />
                                        {doc.document.filename}
                                    </a>
                                ))}
                            </td>
                            <td>{formatDate(submission.created_at)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {isFetchingMore && <div className={styles.loadingMore}>Loading more...</div>}
          </div>
        </div>
      ) : (
        <div className={styles.fileUploadContainer}>
          <div className={styles.fileUpload}>
            <button
              className={styles.fileLabel}
              onClick={() =>
                dispatch(setComponentToShow("UploadStudentDetails"))
              }
            >
              UPLOAD STUDENTS DETAILS
              <img src={fileIcon} alt="Upload" />
            </button>
            <div className={styles.fileInfo}>
              <p>
                Please{" "}
                <a
                  href="src/assets/csvs/studentListUploadTemplate.csv"
                  download
                >
                  click here
                </a>{" "}
                to download a template for .csv file format
              </p>
              <p>File size must be less than 20MB</p>
              <p>Supported format for upload- .csv</p>
            </div>
          </div>
        </div>
      )}
      {renderModals()}
    </div>
  );
}

export default ExamSession;
